/**
 * Final-step confirmation panel for the attach-source flow.
 *
 * Surfaces the toggle / deck activation consents required for the asset to
 * actually be visible to the agent at runtime. The "Resolve gap" button is
 * disabled (with an inline explanation) when the user's choices would leave
 * the asset effectively ignored — mirroring the backend's
 * ``asset_would_be_ignored`` rejection.
 */
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type {
  KGAssetType,
  KGAssetValidationResponse,
  KGRequiredToggleKey,
} from '@/lib/api/knowledgeGaps';

const TOGGLE_LABEL: Record<KGRequiredToggleKey, string> = {
  use_all_videos: 'all videos',
  use_active_deck: 'active decks',
};

const TOGGLE_NOUN: Record<KGRequiredToggleKey, string> = {
  use_all_videos: 'videos',
  use_active_deck: 'decks',
};

const TOGGLE_ASSET_NOUN: Record<KGAssetType, string> = {
  video: 'video',
  presentation_deck: 'document',
};

export function ResolveConfirmationPanel({
  validation,
  enableToggle,
  setEnableToggle,
  activateDeck,
  setActivateDeck,
  onConfirm,
  onBack,
  isSubmitting,
  errorMessage,
}: {
  validation: KGAssetValidationResponse;
  enableToggle: boolean;
  setEnableToggle: (value: boolean) => void;
  activateDeck: boolean;
  setActivateDeck: (value: boolean) => void;
  onConfirm: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}) {
  const showToggleWarning = !validation.required_toggle.currently_enabled;
  const showActivationWarning =
    validation.asset_ref.asset_type === 'presentation_deck' &&
    validation.deck_activation_needed;

  // Mirror the backend "effectively_usable" rule. If the user declined
  // consent for something the asset needs, block the action.
  const toggleSatisfied =
    validation.required_toggle.currently_enabled || enableToggle;
  const activationSatisfied =
    validation.asset_ref.asset_type !== 'presentation_deck' ||
    !validation.deck_activation_needed ||
    activateDeck;
  const canResolve = toggleSatisfied && activationSatisfied;

  let blockedReason: string | null = null;
  if (!toggleSatisfied) {
    const noun = TOGGLE_NOUN[validation.required_toggle.key];
    blockedReason = `Your agent will ignore this ${TOGGLE_ASSET_NOUN[validation.asset_ref.asset_type]} unless the "${TOGGLE_LABEL[validation.required_toggle.key]}" option is enabled.`;
  } else if (!activationSatisfied) {
    blockedReason =
      "This deck won't be visible to your agent unless you activate it.";
  }

  return (
    <div className="flex flex-col gap-5">
      {(showToggleWarning || showActivationWarning) && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold">Before resolving</h4>
          {showToggleWarning && (
            <ToggleConsentRow
              toggleKey={validation.required_toggle.key}
              checked={enableToggle}
              onChange={setEnableToggle}
              disabled={isSubmitting}
            />
          )}
          {showActivationWarning && (
            <DeckActivationConsentRow
              checked={activateDeck}
              onChange={setActivateDeck}
              disabled={isSubmitting}
            />
          )}
        </div>
      )}

      {!showToggleWarning && !showActivationWarning && (
        <p className="text-sm text-muted-foreground">
          This source is already visible to your agent. Resolving will mark
          this gap as resolved with the chosen source attached.
        </p>
      )}

      {errorMessage && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {errorMessage}
        </div>
      )}

      <div className="flex items-center justify-between pt-2 gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <div className="flex items-center gap-3 min-w-0">
          {!canResolve && blockedReason && (
            <span className="text-xs text-muted-foreground text-right">
              {blockedReason}
            </span>
          )}
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={!canResolve || isSubmitting}
          >
            {isSubmitting && (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            )}
            Resolve gap
          </Button>
        </div>
      </div>
    </div>
  );
}

function ToggleConsentRow({
  toggleKey,
  checked,
  onChange,
  disabled,
}: {
  toggleKey: KGRequiredToggleKey;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled: boolean;
}) {
  const label = TOGGLE_LABEL[toggleKey];
  const id = `attach-source-toggle-${toggleKey}`;
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
      <p className="text-sm">
        Your agent has the <span className="font-medium">{label}</span>{' '}
        option turned off, so it currently ignores all{' '}
        {TOGGLE_NOUN[toggleKey]}. Resolving this gap will turn it on.
      </p>
      <label
        htmlFor={id}
        className="flex items-center gap-2 text-sm cursor-pointer"
      >
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(value) => onChange(value === true)}
          disabled={disabled}
        />
        <span>Enable {label} for the agent</span>
      </label>
    </div>
  );
}

function DeckActivationConsentRow({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
      <p className="text-sm">
        This deck isn't currently active. Resolving will activate it so your
        agent can use it. Your other active decks won't be affected.
      </p>
      <label
        htmlFor="attach-source-activate-deck"
        className="flex items-center gap-2 text-sm cursor-pointer"
      >
        <Checkbox
          id="attach-source-activate-deck"
          checked={checked}
          onCheckedChange={(value) => onChange(value === true)}
          disabled={disabled}
        />
        <span>Activate this deck</span>
      </label>
    </div>
  );
}
